import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Divider
} from '@mui/material';

const EditImageDialog = ({ open, onClose, image, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    sort_order: 0,
    external_url: ''
  });

  useEffect(() => {
    if (image) {
      setFormData({
        sort_order: image.sort_order || 0,
        external_url: ''
      });
      setError('');
      setSuccess(false);
      setPreviewUrl('');
    }
  }, [image]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear preview when URL changes
    if (name === 'external_url') {
      setPreviewUrl('');
    }
  };

  const handlePreview = () => {
    if (formData.external_url) {
      setPreviewLoading(true);
      setPreviewUrl(formData.external_url);
    }
  };

  const handleSubmit = async () => {
    if (!image) return;

    // Validate new URL if provided
    if (formData.external_url) {
      try {
        new URL(formData.external_url);
      } catch {
        setError('Please enter a valid URL');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        id: image.id,
        sort_order: parseInt(formData.sort_order) || 0
      };

      // Only include external_url if it's provided
      if (formData.external_url) {
        payload.external_url = formData.external_url.trim();
      }

      console.log('Sending update payload:', payload);

      const response = await fetch('https://api.elnaf.fi/api/update_product_image.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      console.log('Response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        setError('Invalid response from server');
        setLoading(false);
        return;
      }

      if (response.ok && (data.message?.includes('uğurla') || data.message?.includes('yeniləndi'))) {
        setSuccess(true);
        if (data.updated_url) {
          console.log('Updated image URL:', data.updated_url);
        }
        setTimeout(() => {
          onSave();
        }, 1500);
      } else {
        setError(data.message || 'Failed to update image');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !success) {
      setError('');
      setSuccess(false);
      setPreviewUrl('');
      setPreviewLoading(false);
      onClose();
    }
  };

  if (!image) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit Product Image
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          SKU: {image.sku}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Image updated successfully!
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Image:
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 200,
                mx: 'auto',
                border: '1px solid #ddd',
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              <img
                src={image.image_url}
                alt={`Product ${image.sku}`}
                style={{ width: '100%', display: 'block' }}
              />
            </Box>
          </Box>

          <TextField
            fullWidth
            label="Sort Order"
            name="sort_order"
            type="number"
            value={formData.sort_order}
            onChange={handleChange}
            margin="normal"
            disabled={loading || success}
            InputProps={{ inputProps: { min: 0 } }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            Replace Image (Optional)
          </Typography>
          
          <TextField
            fullWidth
            label="New External Image URL"
            name="external_url"
            value={formData.external_url}
            onChange={handleChange}
            margin="normal"
            disabled={loading || success}
            placeholder="https://example.com/new-image.jpg"
            helperText="Leave empty to keep the current image"
          />

          {formData.external_url && (
            <Box sx={{ mt: 1 }}>
              <Button
                onClick={handlePreview}
                disabled={loading || success}
                size="small"
              >
                Preview New Image
              </Button>
            </Box>
          )}

          {previewUrl && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2" gutterBottom>
                New Image Preview:
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 200,
                  mx: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5'
                }}
              >
                {previewLoading && (
                  <Box sx={{ p: 4 }}>
                    <CircularProgress />
                  </Box>
                )}
                <img
                  src={previewUrl}
                  alt="New Preview"
                  style={{
                    width: '100%',
                    display: previewLoading ? 'none' : 'block'
                  }}
                  onLoad={() => setPreviewLoading(false)}
                  onError={() => {
                    setPreviewLoading(false);
                    setError('Failed to load image preview. Please check the URL.');
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || success}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {success ? 'Saved!' : 'Update Image'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditImageDialog;